import type { ReactNode, SVGProps } from "react";

/**
 * The hand-drawn duotone glyphs of the labels (spec-64-ul): `currentColor` strokes with a
 * translucent fill, `aria-hidden`, sized by the parent (`.vonk-badge__icon svg`, `.vonk-nda`).
 * Paths are copied from the chosen design's symbols; colours come from the caller or tokens.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

const stroked = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

export function SparkleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2.5l2.1 6.2 6.4 2.3-6.4 2.3L12 19.5l-2.1-6.2L3.5 11l6.4-2.3Z" />
      <path
        d="M19 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8Z"
        opacity=".6"
      />
    </svg>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <svg {...stroked} {...props}>
      <path
        d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
        fill="currentColor"
        fillOpacity=".18"
      />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function DocIcon(props: IconProps) {
  return (
    <svg {...stroked} {...props}>
      <path
        d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"
        fill="currentColor"
        fillOpacity=".15"
      />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  );
}

/** NDA "none": a dashed empty seal with a slash. */
export function SealNoneIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <circle
        cx="12"
        cy="12"
        r="9.5"
        fill="none"
        strokeWidth="1.6"
        strokeDasharray="2 3"
        style={{ stroke: "var(--nda-none-seal)" }}
      />
      <path
        d="M8.5 15.5l7-7"
        strokeWidth="1.8"
        strokeLinecap="round"
        style={{ stroke: "var(--nda-none-slash)" }}
      />
    </svg>
  );
}

/** NDA "required" (gate not met): the seal ring of "Geen NDA" in warm amber, static, with an alert mark. */
export function SealWarnIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <circle
        cx="12"
        cy="12"
        r="9.5"
        fill="none"
        strokeWidth="1.6"
        strokeDasharray="2 3"
        style={{ stroke: "var(--nda-warn-ring)" }}
      />
      <path
        d="M12 7.5v5.2M12 16.3h.01"
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        style={{ stroke: "var(--nda-warn-ring)" }}
      />
    </svg>
  );
}

/** Gesprek "planned": a duotone calendar with a clock, in the NDA-running blue. */
export function CalendarClockIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" {...props}>
      <g
        stroke="var(--nda-run-ring)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect
          x="3"
          y="4.5"
          width="16"
          height="15"
          rx="2.5"
          fill="var(--nda-run-ring)"
          fillOpacity=".18"
        />
        <path d="M7.5 2.8v3.4M14.5 2.8v3.4M3 9.5h16" />
        <circle cx="17" cy="17" r="5" fill="#fff" />
        <circle cx="17" cy="17" r="5" fill="var(--nda-run-ring)" fillOpacity=".18" />
        <path d="M17 14.4V17l1.7 1" />
      </g>
    </svg>
  );
}

/** Gesprek "held": two duotone speech bubbles with a small check, in the NDA-signed green. */
export function ChatCheckIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" {...props}>
      <g
        stroke="var(--nda-signed-ink-stroke)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          d="M3 5.5A2.5 2.5 0 0 1 5.5 3h8A2.5 2.5 0 0 1 16 5.5v4a2.5 2.5 0 0 1-2.5 2.5H8l-3 2.5V12A2.5 2.5 0 0 1 3 9.5Z"
          fill="var(--nda-signed-ink-stroke)"
          fillOpacity=".18"
        />
        <path
          d="M19 9.5h.5A2.5 2.5 0 0 1 22 12v4a2.5 2.5 0 0 1-2 2.45V21l-3-2.5h-5A2.5 2.5 0 0 1 9.5 16v-.5"
          fill="var(--nda-signed-ink-stroke)"
          fillOpacity=".1"
        />
        <path d="m6.6 7.6 1.7 1.7 3.2-3.4" />
      </g>
    </svg>
  );
}

/** NDA "running": a dashed ring (spun by CSS) around a small document. */
export function SealRunningIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <circle
        className="vonk-nda__ring"
        cx="12"
        cy="12"
        r="9.5"
        fill="none"
        strokeWidth="1.8"
        strokeDasharray="4 3"
        strokeLinecap="round"
        style={{ stroke: "var(--nda-run-ring)" }}
      />
      <g
        transform="translate(6.5 6.5) scale(.46)"
        style={{ color: "var(--nda-run-ink)" }}
      >
        <DocIcon width={24} height={24} />
      </g>
    </svg>
  );
}

/** NDA "signed": a scribble that draws itself once (CSS). */
export function SignatureIcon(props: IconProps) {
  return (
    <svg
      className="vonk-nda__scribble"
      viewBox="0 0 34 14"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M1 10c3-6 5-8 6-6s-2 7 0 7 4-8 6-8-1 9 1 9 3-5 5-5 1 4 3 4 4-3 6-3 3 2 5 1"
        fill="none"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: "var(--nda-signed-ink-stroke)" }}
      />
    </svg>
  );
}

/**
 * The VONK flame. The caller owns the gradient (it must live in the same `<svg>` with a
 * per-instance id, see Chip); without `gradientId` the flame takes `currentColor`.
 */
export function FlameIcon({
  gradientId,
  children,
  ...props
}: IconProps & { gradientId?: string; children?: ReactNode }) {
  return (
    <svg viewBox="0 0 12 16" aria-hidden="true" {...props}>
      {children}
      <path
        d="M6.9.6c.35 2.1-.5 3.2-1.7 4.5C3.5 6.9 1 8.9 1 11.6a5 5 0 0 0 10 .1c0-2.4-1.4-4-2.4-5.3.15 1.2-.3 2.1-1 2.6C8 6.1 7.6 3 6.9.6Z"
        fill={gradientId ? `url(#${gradientId})` : "currentColor"}
      />
    </svg>
  );
}
